/**
 * @file app/tauri/integrationsConfig.js
 *
 * Central, overridable configuration for the desktop hardware integrations.
 *
 * Secrets are sourced from build-time env vars (webpack `DefinePlugin` /
 * `process.env`) so they are NOT hardcoded into the committed source. A
 * last-resort fallback to the shared default keeps existing terminals working,
 * but emits a one-time warning so the deployment is flagged. Per-restaurant
 * overrides always win: pass `terminalConfig.secret` from the saved settings.
 */

const tag = "[integrationsConfig]";

/**
 * The shared Novo/Wallee terminal HMAC secret. Prefer, in order:
 *   1. `terminalConfig.secret` passed at call time (per-restaurant),
 *   2. `process.env.HAPPY_NOVO_TERMINAL_SECRET` (build-time injected),
 *   3. the legacy shared default (warns once).
 * @type {string}
 */
let warnedDefault = false;
export const getNovoTerminalSecret = (perCallSecret) => {
  if (perCallSecret) return perCallSecret;
  const envSecret =
    typeof process !== "undefined" && process.env
      ? process.env.HAPPY_NOVO_TERMINAL_SECRET
      : undefined;
  if (envSecret) return envSecret;
  if (!warnedDefault) {
    warnedDefault = true;
    // eslint-disable-next-line no-console
    console.warn(
      `${tag} using the built-in default Novo terminal secret — set HAPPY_NOVO_TERMINAL_SECRET or a per-restaurant terminalConfig.secret in production.`
    );
  }
  return "LdGrupcWWeu0M4c77uAqPdwQY+RkySQq6+QNNdOqazQ=";
};

/** True when the app is served from an HTTPS origin (browser/PWA build). */
const isSecurePage = () =>
  typeof window !== "undefined" &&
  window.location &&
  window.location.protocol === "https:";

/** Raw (possibly insecure) MQTT sync broker, before any HTTPS upgrade. */
const RAW_MQTT_BROKER_URL =
  (typeof process !== "undefined" && process.env && process.env.HAPPY_MQTT_URL) ||
  "wss://sync.happysolutions.hu/mqtt";

/**
 * Resolve the MQTT broker URL for the current runtime.
 *
 * A page loaded over HTTPS may not open an insecure `ws://` WebSocket (browser
 * mixed-content policy), which otherwise throws `SecurityError` and forces the
 * realtime layer onto slow polling. When we detect a secure page we transparently
 * upgrade `ws://` → `wss://` (and `mqtt://` → `mqtts://`). An explicit secure URL
 * from `HAPPY_MQTT_URL` is always respected as-is.
 * @returns {string}
 */
export const resolveMqttBrokerUrl = () => {
  let url = RAW_MQTT_BROKER_URL;
  if (isSecurePage()) {
    if (url.startsWith("ws://")) url = "wss://" + url.slice("ws://".length);
    else if (url.startsWith("mqtt://")) url = "mqtts://" + url.slice("mqtt://".length);
  }
  return url;
};

/** Default MQTT sync broker (WebSocket) for desktop — HTTPS-aware. */
export const MQTT_BROKER_URL = resolveMqttBrokerUrl();

/** Merchant reference sent with Wallee/SCA purchases (Hungarian acquirer id). */
export const NOVO_MERCHANT_REFERENCE =
  (typeof process !== "undefined" && process.env && process.env.HAPPY_NOVO_MERCHANT_REF) ||
  "2101687820";

export default { getNovoTerminalSecret, MQTT_BROKER_URL, resolveMqttBrokerUrl, NOVO_MERCHANT_REFERENCE };
